import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useCurrentInternalUser } from '../lib/currentUser';
import { parseSuppliersDrillParams, SUPPLIERS_BANNER_DEFAULTS, supplierOpStatus } from '../lib/reportingDrill';
import HowItWorksButton from '../components/HowItWorksButton';
import Sidebar from '../components/Sidebar';
import FilterDropdown from '../components/FilterDropdown';
import SearchInput from '../components/SearchInput';
import ComplianceBadge from '../components/compliance/ComplianceBadge';
import BadgeChip from '../components/BadgeChip';
import TagChip from '../components/TagChip';
import TagFilterBar from '../components/TagFilterBar';
import AddButton from '../components/AddButton';
import GroupBySelect from '../components/GroupBySelect';
import GroupedListTable from '../components/GroupedListTable';
import TagPicker from '../components/TagPicker';
import { fetchTagsFor, applyTag } from '../lib/tags';
import { fmtRelativeDays } from '../lib/dates';

import { PinButton } from '../components/pinBurst';
import { OttoLoading, OttoPageBar, OttoConfused } from '../components/otto';
// labelForDocumentKey is now sourced from a fusion_documents lookup loaded
// in this component, so adding a doc via Admin > Fusion Documents flows
// through to the Awaiting Review queue label without a code change.

// Avatar palette for the supplier-row avatar circles. Deterministically
// chosen from supplier.id so a given supplier always lands on the same
// colour across reloads. Reds excluded - this is a neutral identifier,
// not a warning.
const AVATAR_PALETTE = [
  { bg: '#92D400', text: '#131313' }, // Fusion Green
  { bg: '#2E8FA1', text: '#F9FCF1' }, // Teal
  { bg: '#E89A2E', text: '#131313' }, // Amber
  { bg: '#009159', text: '#F9FCF1' }, // Deep Green
];
const supplierInitials = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  return '?';
};
const supplierAvatarPalette = (id) => {
  const s = String(id || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) & 0x7fffffff;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

// Maps a supplier row to the canonical pill bucket. Active = finance-
// approved and not inactive. Onboarding = active row that hasn't been
// finance-approved yet (the closest concept the current schema has to
// "still setting up"). Inactive = explicit status flip.
const statusPillFor = (s) => {
  if (s.status === 'inactive') return 'inactive';
  if (s.finance_approved === true) return 'active';
  return 'onboarding';
};

// Last activity column — combines the shared relative-days formatter
// (Today / Yesterday / N days ago / N weeks ago with correct singular
// / plural handling) with a tone class so recent rows read white,
// mid-range rows read grey, and stale rows fade out at 60% opacity.
const formatLastActivity = (at) => {
  if (!at) return null;
  const text = fmtRelativeDays(at);
  if (!text) return null;
  const days = Math.floor((Date.now() - new Date(at).getTime()) / 86400000);
  let tone;
  if (days < 14) tone = 'text-ink';
  else if (days < 30) tone = 'text-fusion-mid-grey';
  else tone = 'text-[#51534A] opacity-60';
  return { text, tone };
};

const PAGE_SIZE = 25;

export default function Suppliers() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Per-column filters on the List View. Each is a multi-select; rows
  // must match every column with active filters (AND across columns; OR
  // within a column).
  const [regionFilter, setRegionFilter] = useState([]);
  const [riskFilter, setRiskFilter] = useState([]);
  const [complianceColumnFilter, setComplianceColumnFilter] = useState([]);
  // Tags: per-supplier tag map, the active tag filter (AND), and bulk-apply
  // selection state.
  //
  // NOTE — divergence from the Briefs list (deliberate, per the
  // 2026-06 list-page consistency review): Suppliers carry per-row
  // checkboxes that drive the "Apply tag" bulk action below; Briefs
  // have no equivalent bulk capability and therefore no selection
  // column. This is a real feature difference, not a styling drift.
  // If a bulk action ever lands on Briefs (e.g. bulk archive), both
  // pages should share the selection paradigm.
  const [tagsBySupplier, setTagsBySupplier] = useState(new Map());
  const [supplierTagFilter, setSupplierTagFilter] = useState([]);
  const [selectedForBulk, setSelectedForBulk] = useState(() => new Set());
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);
  // Per-user pin set so the leftmost row icon can toggle without
  // opening the supplier detail page. Same pattern as the Briefs list.
  const [userPinnedSupplierIds, setUserPinnedSupplierIds] = useState(new Set());

  useEffect(() => {
    if (!currentInternalUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_pinned_suppliers')
        .select('supplier_id')
        .eq('user_id', currentInternalUser.id);
      if (cancelled) return;
      setUserPinnedSupplierIds(new Set((data || []).map(r => r.supplier_id)));
    })();
    return () => { cancelled = true; };
  }, [currentInternalUser?.id]);

  const toggleSupplierPin = async (supplierId, e) => {
    if (e) e.stopPropagation();
    if (!currentInternalUser?.id || !supplierId) return;
    const wasPinned = userPinnedSupplierIds.has(supplierId);
    setUserPinnedSupplierIds(prev => {
      const next = new Set(prev);
      if (wasPinned) next.delete(supplierId); else next.add(supplierId);
      return next;
    });
    const { error } = wasPinned
      ? await supabase
          .from('user_pinned_suppliers')
          .delete()
          .eq('user_id', currentInternalUser.id)
          .eq('supplier_id', supplierId)
      : await supabase
          .from('user_pinned_suppliers')
          .upsert({ user_id: currentInternalUser.id, supplier_id: supplierId }, { onConflict: 'user_id,supplier_id' });
    if (error) {
      setUserPinnedSupplierIds(prev => {
        const next = new Set(prev);
        if (wasPinned) next.add(supplierId); else next.delete(supplierId);
        return next;
      });
    }
  };
  // Reporting drill-down filters — silent (no toolbar UI), only set by
  // dashboard tile clicks landing here via location.state.reportingDrill.
  //   idAllowlistFilter — Set<supplier_id>; suppliers must be in the set
  //   createdSinceFilter — Date; supplier.created_at >= this
  //   drillBanner — { tone, descriptor } for the contextual banner above
  //                 the list, mirroring the Briefs page drill banner UX
  const [idAllowlistFilter, setIdAllowlistFilter] = useState(null);
  const [createdSinceFilter, setCreatedSinceFilter] = useState(null);
  const [drillBanner, setDrillBanner] = useState(null);
  // Drill-only filters introduced for the new SuppliersReporting (stage 4).
  // Like idAllowlist + createdSince, these are silent (no toolbar UI) and
  // only set by Reporting drill landings via URL params. clearDrillFilters
  // wipes them alongside the others.
  const [opStatusFilter,   setOpStatusFilter]   = useState(null); // 'active' | 'preferred' | ...
  const [tierFilter,       setTierFilter]       = useState(null); // 1 | 2
  const [dormantFilter,    setDormantFilter]    = useState(null); // '90d' | '180d'
  // Under-utilised: drill lands with ?briefs_invited_in_90d=0 from the
  // Suppliers Reporting "active never invited" card. When true, filter
  // out any supplier whose 90-day invited count is >0.
  const [underUtilisedFilter, setUnderUtilisedFilter] = useState(false);
  // Sticky flag set when the page is reached via a Reporting drill-down
  // (URL contains ?ref=reporting). Drives the Back-to-Reporting affordance.
  // Stays set even after the user clears filters per spec — link remains
  // available until the user explicitly returns.
  const [fromReporting, setFromReporting] = useState(false);
  // Map of compliance_doc_key → human label, sourced from fusion_documents.
  // Passed to ComplianceOverview so its doc rows render the human name
  // rather than the raw key. Falls back to the key if a doc has been
  // deleted but onboarding rows still reference it.
  // Current badges for every supplier, grouped by supplier_id so each row
  // renders its own chips. Side-fetched (cheap query) so the list can stay
  // independent of the SuppliersReporting badge fetch.
  const [badgesBySupplier, setBadgesBySupplier] = useState(new Map());
  const [badgeCategoryMap, setBadgeCategoryMap] = useState(new Map());
  // Primary status filter as a single-select pill ('active' / 'onboarding'
  // / 'inactive' / 'all'). Composes with Group By: the status pill
  // FILTERS the list, Group By organises whatever's left.
  const [statusPill, setStatusPill] = useState('all');
  // Group By selection (2026-06 unified grouped-list brief). Defaults
  // to 'pinned' so the page opens with the PINNED group at the top —
  // matches the Briefs list's same default and the user's expected
  // "open with pinned visible" behaviour. Other options group by
  // entity-appropriate keys: status / risk / compliance / region.
  const [groupBy, setGroupBy] = useState('pinned');
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroupCollapsed = (key) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Custom sort column. Null = default order (pinned grouped at the top
  // under a section header, then the rest alphabetically by name). When
  // set, the section headers disappear and the list sorts as a flat
  // table by the chosen column.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  // Pagination - the pinned section is always fully visible at the top
  // (it's a favourites list, not subject to pages); only the
  // ALL-OTHER-ACTIVE section is paginated. Page resets to 1 whenever
  // filters / sort / search change.
  const [currentPage, setCurrentPage] = useState(1);
  // Which row's three-dot action menu is currently open. Null = none.
  const [rowActionsFor, setRowActionsFor] = useState(null);
  // Otto lifecycle bar — × hides it for the rest of the day (per user).
  const [ottoBarDismissedOn, setOttoBarDismissedOn] = useState(() => {
    try { return sessionStorage.getItem('otto_suppliers_bar_dismissed'); } catch (e) { return null; }
  });
  const rowActionsRef = useRef(null);

  useEffect(() => {
    getSuppliers();
  }, []);

  // Reporting drill-down — when the user lands here from a Briefs Reporting
  // tile, location.state.reportingDrill carries the filter descriptor.
  // Applied once on mount; clearing the banner wipes the drill filters but
  // leaves the user's manually-selected filters intact.
  useEffect(() => {
    const drill = location.state?.reportingDrill;
    // Compliance Overview (now on /compliance) sends a traffic-light filter
    // via location.state.complianceFilter when a tile is clicked.
    const cf = location.state?.complianceFilter;
    if (!drill && !cf) return;
    if (drill) {
      if (drill.idAllowlist) setIdAllowlistFilter(new Set(drill.idAllowlist));
      // BriefReporting drills used to pass an array of advanced-status values
      // here. The advanced-status filter has been dropped, so any drill that
      // still passes statusFilter is reinterpreted as a primary status-pill
      // selection when the array contains a single primary status value.
      if (drill.statusPill) setStatusPill(drill.statusPill);
      else if (drill.statusFilter && drill.statusFilter.length === 1 &&
          ['active', 'inactive', 'onboarding'].includes(drill.statusFilter[0])) {
        setStatusPill(drill.statusFilter[0]);
      }
      if (drill.createdSince) setCreatedSinceFilter(new Date(drill.createdSince));
      if (drill.banner) setDrillBanner(drill.banner);
    }
    // Apply the compliance traffic-light filter. 'not_set' has no list value
    // to match, so it clears the filter (shows all) — mirrors the previous
    // in-page Compliance Overview behaviour.
    if (cf === 'red')        setComplianceColumnFilter(['red']);
    else if (cf === 'amber') setComplianceColumnFilter(['amber']);
    else if (cf === 'green') setComplianceColumnFilter(['green']);
    else if (cf === 'not_set') setComplianceColumnFilter([]);
    // Drop the location state so refreshing the page doesn't re-apply the
    // drill / filter. The filters stick because they're held in component state.
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDrillFilters = () => {
    setIdAllowlistFilter(null);
    setCreatedSinceFilter(null);
    setDrillBanner(null);
    setOpStatusFilter(null);
    setTierFilter(null);
    setDormantFilter(null);
    setUnderUtilisedFilter(false);
  };

  // Back-to-Reporting handler — swaps to the Reporting tab in-place,
  // Reporting now lives at /reporting (System nav), so a drill that
  // arrived here from the Suppliers tab of that page returns to the
  // same sub-tab. Clear the local drill before navigating away.
  const handleBackToReporting = () => {
    clearDrillFilters();
    setFromReporting(false);
    setSearchParams(new URLSearchParams(), { replace: true });
    navigate('/reporting?tab=suppliers');
  };

  // Mount-once URL-param read. Translates the canonical drill keys
  // (see src/lib/reportingDrill.js) into local filter state, alongside
  // the existing location.state path. Runs exactly once via a ref
  // guard so subsequent setSearchParams calls don't re-fire mid-
  // interaction.
  const initialDrillReadRef = useRef(false);
  useEffect(() => {
    if (initialDrillReadRef.current) return;
    initialDrillReadRef.current = true;
    const drill = parseSuppliersDrillParams(searchParams);
    // Multi-select column filters take arrays — wrap singular params
    // so the chip UI renders the value as a removable filter chip.
    // Category was dropped along with the advanced-filters popover;
    // any drill that still passes ?category=... is silently ignored.
    if (drill.region)     setRegionFilter([drill.region]);
    if (drill.compliance) setComplianceColumnFilter([drill.compliance]);
    // New SuppliersReporting drill params (stage 4) — these were read
    // but unwired in stage 1; the new component fires drills that
    // actually expect them to filter. supplierOpStatus() in the filter
    // chain below synthesises op_status from the multi-column row.
    if (drill.opStatus) setOpStatusFilter(drill.opStatus);
    if (drill.tier)     setTierFilter(drill.tier);
    if (drill.dormant)  setDormantFilter(drill.dormant);
    if (drill.briefsInvitedIn90d === '0') setUnderUtilisedFilter(true);
    if (drill.createdWithin) {
      const map = { '30d': 30, '90d': 90, '365d': 365 };
      const days = map[drill.createdWithin];
      if (days) {
        const since = new Date();
        since.setDate(since.getDate() - days);
        setCreatedSinceFilter(since);
      }
    }
    if (drill.fromReporting) {
      setFromReporting(true);
      // Banner precedence: explicit descriptor + tone in URL wins
      // (rich copy survives refresh), then the named banner key
      // lookup, then a generic fallback.
      let banner;
      if (drill.bannerDescriptor) {
        banner = { tone: drill.bannerTone || 'teal', descriptor: drill.bannerDescriptor };
      } else if (drill.banner && SUPPLIERS_BANNER_DEFAULTS[drill.banner]) {
        banner = SUPPLIERS_BANNER_DEFAULTS[drill.banner];
      } else {
        banner = { tone: 'teal', descriptor: 'from Reporting' };
      }
      setDrillBanner(banner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the filter / sort / search inputs change, jump back to
  // page 1 - otherwise a user paging through the list, then narrowing
  // by category, would land on an empty later page.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusPill, regionFilter, riskFilter, complianceColumnFilter, search, sortColumn, sortDirection]);

  // Close the row-actions popover on outside click or Escape so it
  // doesn't trap input. Standard menu affordance.
  useEffect(() => {
    if (!rowActionsFor) return;
    const onClick = (e) => {
      if (rowActionsRef.current && !rowActionsRef.current.contains(e.target)) setRowActionsFor(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setRowActionsFor(null); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [rowActionsFor]);

  // Load the current supplier badge state + category metadata once.
  // Each row's chip cluster reads off these maps. Expired badges are
  // filtered out at the list-row layer (current chips only); the
  // SupplierDetail achievements strip surfaces the full history.
  useEffect(() => {
    const loadBadges = async () => {
      const [badgesRes, catsRes] = await Promise.all([
        supabase.from('supplier_badges').select('supplier_id, category_id, tier, expires_at'),
        supabase.from('badge_categories').select('id, slug, name'),
      ]);
      const catMap = new Map((catsRes.data || []).map(c => [c.id, c]));
      setBadgeCategoryMap(catMap);
      const grouped = new Map();
      const now = Date.now();
      for (const b of badgesRes.data || []) {
        if (b.expires_at && new Date(b.expires_at).getTime() < now) continue;
        if (!grouped.has(b.supplier_id)) grouped.set(b.supplier_id, []);
        grouped.get(b.supplier_id).push(b);
      }
      setBadgesBySupplier(grouped);
    };
    loadBadges();
  }, []);

  const getSuppliers = async () => {
    setLoading(true);
    // Pull suppliers + the aggregate inputs the Reporting tab needs in
    // parallel. brief_recipients gives "last brief activity" via notified_at;
    // brief_responses gives the response leaderboard; closed-and-awarded
    // briefs give the awarded-value leaderboard. All three side-tables are
    // small (a row per (brief, supplier) pairing or per brief), so fetching
    // them whole is fine.
    const [suppliersRes, recipientsRes, responsesRes, awardedBriefsRes] = await Promise.all([
      // Use select('*') so the page is resilient to schema variation —
      // referring to a column that hasn't been added yet (e.g. tier,
      // archived_at) by an unrun migration would error the whole
      // SELECT and blank out the list page. With '*' we get whatever
      // columns exist; downstream code that reads optional columns
      // already handles undefined.
      supabase
        .from('suppliers')
        .select('*')
        .order('supplier_name'),
      supabase.from('brief_recipients').select('supplier_id, notified_at'),
      supabase.from('brief_responses').select('supplier_id, submitted_at').eq('is_submitted', true),
      supabase.from('briefs').select('awarded_supplier_id, awarded_amount').eq('status', 'closed').not('awarded_supplier_id', 'is', null),
    ]);
    if (suppliersRes.error) {
      // Surface load failures rather than silently rendering "no suppliers yet" —
      // the empty state previously masked schema-mismatch errors entirely.
      // eslint-disable-next-line no-console
      console.error('Suppliers load failed', suppliersRes.error);
      setLoading(false);
      return;
    }

    // Build per-supplier aggregates: response_count, last_activity_at
    // (most recent of any notified_at or response submitted_at), and
    // invited_count_90d (drives the under-utilised drill on Suppliers
    // Reporting — "active suppliers never invited to a brief in 90d").
    const responseCounts = new Map();
    for (const r of responsesRes.data || []) {
      responseCounts.set(r.supplier_id, (responseCounts.get(r.supplier_id) || 0) + 1);
    }
    const lastActivity = new Map();
    const recordActivity = (supplierId, iso) => {
      if (!supplierId || !iso) return;
      const prev = lastActivity.get(supplierId);
      if (!prev || new Date(iso) > new Date(prev)) lastActivity.set(supplierId, iso);
    };
    const cutoff90 = Date.now() - 90 * 86400000;
    const invitedCount90 = new Map();
    for (const r of recipientsRes.data || []) {
      recordActivity(r.supplier_id, r.notified_at);
      if (r.supplier_id && r.notified_at && new Date(r.notified_at).getTime() >= cutoff90) {
        invitedCount90.set(r.supplier_id, (invitedCount90.get(r.supplier_id) || 0) + 1);
      }
    }
    for (const r of responsesRes.data || []) recordActivity(r.supplier_id, r.submitted_at);

    // Awarded-work leaderboard inputs: count of closed-and-awarded briefs per
    // supplier and total £ across them. Briefs without an awarded_amount
    // contribute to the count but not the total — useful for "awarded the
    // job but never logged the figure" suppliers.
    const awardedCount = new Map();
    const awardedTotal = new Map();
    for (const b of awardedBriefsRes.data || []) {
      if (!b.awarded_supplier_id) continue;
      awardedCount.set(b.awarded_supplier_id, (awardedCount.get(b.awarded_supplier_id) || 0) + 1);
      const amt = Number(b.awarded_amount);
      if (Number.isFinite(amt) && amt > 0) {
        awardedTotal.set(b.awarded_supplier_id, (awardedTotal.get(b.awarded_supplier_id) || 0) + amt);
      }
    }

    setSuppliers((suppliersRes.data || []).map(s => ({
      ...s,
      response_count: responseCounts.get(s.id) || 0,
      last_activity_at: lastActivity.get(s.id) || null,
      awarded_count: awardedCount.get(s.id) || 0,
      awarded_total: awardedTotal.get(s.id) || 0,
      invited_count_90d: invitedCount90.get(s.id) || 0,
    })));
    setLoading(false);
    // Tags load in the background — don't block the list on it.
    const ids = (suppliersRes.data || []).map(s => s.id);
    fetchTagsFor('supplier', ids).then(setTagsBySupplier);
  };

  const toggleBulk = (id) => setSelectedForBulk(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Apply one tag to every bulk-selected supplier (idempotent). Updates the
  // local tag map so chips appear immediately; the picker stays open.
  const handleBulkApply = async (tag) => {
    const ids = [...selectedForBulk];
    await Promise.all(ids.map(id => applyTag('supplier', id, tag.id, currentInternalUser?.id)));
    setTagsBySupplier(prev => {
      const next = new Map(prev);
      for (const id of ids) {
        const cur = next.get(id) || [];
        if (!cur.some(t => t.id === tag.id)) next.set(id, [...cur, tag].sort((a, b) => a.name.localeCompare(b.name)));
      }
      return next;
    });
  };

  // Distinct region values present in the loaded supplier set, sorted —
  // drives the Region column filter's options. Computed every render
  // since the supplier set is already in memory and the cost is trivial.
  const regionOptions = Array.from(new Set(suppliers.map(s => s.region).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b))
    .map(r => ({ value: r, label: r }));
  // Primary status pill - the canonical Active / Onboarding / Inactive
  // / All choice driving the toolbar. 'all' bypasses the check entirely.
  // Counts surface alongside each pill so the user sees how many rows
  // sit in each bucket before clicking through.
  const pillCounts = suppliers.reduce((acc, s) => {
    const b = statusPillFor(s);
    acc[b] = (acc[b] || 0) + 1;
    acc.all += 1;
    return acc;
  }, { active: 0, onboarding: 0, inactive: 0, all: 0 });

  const query = search.trim().toLowerCase();
  const filtered = suppliers.filter(s => {
    if (statusPill !== 'all' && statusPillFor(s) !== statusPill) return false;
    // Deactivated / inactive suppliers drop out of the default "all" view —
    // they stay reachable via the Inactive status pill.
    if (statusPill === 'all' && statusPillFor(s) === 'inactive') return false;
    if (regionFilter.length > 0 && !regionFilter.includes(s.region)) return false;
    if (riskFilter.length > 0 && !riskFilter.includes(s.risk_flag)) return false;
    if (complianceColumnFilter.length > 0 && !complianceColumnFilter.includes(s.compliance_status)) return false;
    // Reporting drill: restrict to a specific set of supplier_ids
    // (e.g. "suppliers who received briefs in last 90 days").
    if (idAllowlistFilter && !idAllowlistFilter.has(s.id)) return false;
    // Reporting drill: created on/after a given date (e.g. "New This Quarter").
    if (createdSinceFilter) {
      if (!s.created_at) return false;
      if (new Date(s.created_at) < createdSinceFilter) return false;
    }
    // New SuppliersReporting drills (stage 4) — op_status / tier / dormant.
    // opStatus uses the multi-column synthesiser so a single canonical
    // enum value maps onto whichever DB column actually drives the state.
    if (opStatusFilter && supplierOpStatus(s) !== opStatusFilter) return false;
    if (tierFilter != null && s.tier !== tierFilter) return false;
    if (dormantFilter) {
      const days = dormantFilter === '180d' ? 180 : 90;
      const cutoff = Date.now() - days * 86400000;
      const lastActive = s.last_activity_at ? new Date(s.last_activity_at).getTime() : 0;
      if (lastActive >= cutoff) return false;
      // Only count active suppliers as eligible for "dormant" — blocked
      // / on-hold rows aren't dormant, they're explicitly paused.
      if (s.status !== 'active' || s.on_hold || s.do_not_use_status === 'Do Not Use') return false;
    }
    // Under-utilised: zero brief invites in the last 90 days. Computed
    // upstream in getSuppliers() from brief_recipients.notified_at.
    if (underUtilisedFilter && (s.invited_count_90d || 0) > 0) return false;
    // Tag filter — AND semantics: the supplier must carry every selected tag.
    if (supplierTagFilter.length > 0) {
      const ids = new Set((tagsBySupplier.get(s.id) || []).map(t => t.id));
      if (!supplierTagFilter.every(id => ids.has(id))) return false;
    }
    if (query) {
      const hit = [s.supplier_name, s.category, s.main_contact_name, s.region]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(query));
      if (!hit) return false;
    }
    return true;
  });
  const filtersActive = Boolean(query) || statusPill !== 'all' || regionFilter.length > 0 || riskFilter.length > 0 || complianceColumnFilter.length > 0 || supplierTagFilter.length > 0 || idAllowlistFilter || createdSinceFilter || drillBanner || opStatusFilter || tierFilter != null || dormantFilter || underUtilisedFilter;

  // Otto lifecycle briefing bar (build #9). Priority ladder surfaces the
  // single top item: onboarding stalled 30+ days → new suppliers this
  // week → all clear. (Oak Status wins are celebrated at approval time
  // via OttoAward; a live bar state for them needs an eco-approval
  // timestamp, tracked separately.) This is a lifecycle signal, never a
  // compliance one — compliance lives on the supplier record.
  const todayKey = new Date().toDateString();
  const ottoBarHidden = ottoBarDismissedOn === todayKey;
  const supplierLifecycle = (() => {
    const now = Date.now();
    const stalled = suppliers.filter(s => statusPillFor(s) === 'onboarding' && s.created_at && (now - new Date(s.created_at).getTime()) > 30 * 86400000).length;
    const newThisWeek = suppliers.filter(s => s.created_at && (now - new Date(s.created_at).getTime()) <= 7 * 86400000).length;
    if (stalled > 0) return { dot: 'teal', title: `${stalled} supplier${stalled === 1 ? ' has' : 's have'} been onboarding 30+ days`, body: 'Worth a chase to get them over the line.', ctaLabel: 'View onboarding →', onCta: () => setStatusPill('onboarding') };
    if (newThisWeek > 0) return { dot: 'green', title: `${newThisWeek} new supplier${newThisWeek === 1 ? '' : 's'} joined this week`, body: 'Say hello and help them get set up.', ctaLabel: 'View onboarding →', onCta: () => setStatusPill('onboarding') };
    return { dot: null, title: "Your roster's looking healthy", body: 'No onboarding stalls and nothing new to chase right now.' };
  })();
  const dismissOttoBar = () => {
    try { sessionStorage.setItem('otto_suppliers_bar_dismissed', todayKey); } catch (e) { /* ignore */ }
    setOttoBarDismissedOn(todayKey);
  };

  // Sort + section + pagination derivation. Section headers (PINNED /
  // ALL OTHER ACTIVE) only render under the default sort - clicking a
  // column header to sort drops the sections and treats the list as
  // flat. Pinned suppliers are always fully visible at the top; only
  // the others section paginates.
  const sortAccessor = (s) => {
    switch (sortColumn) {
      case 'name':       return (s.supplier_name || '').toLowerCase();
      case 'region':     return (s.region || '').toLowerCase();
      case 'status':     return statusPillFor(s);
      case 'risk':       return ({ high: 0, medium: 1, low: 2 })[s.risk_flag] ?? 3;
      case 'compliance': return ({ red: 0, amber: 1, green: 2 })[s.compliance_status] ?? 3;
      case 'activity':   return s.last_activity_at ? -new Date(s.last_activity_at).getTime() : Number.POSITIVE_INFINITY;
      default:           return (s.supplier_name || '').toLowerCase();
    }
  };
  const sortedFiltered = [...filtered].sort((a, b) => {
    const av = sortAccessor(a);
    const bv = sortAccessor(b);
    let cmp;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv));
    return sortDirection === 'desc' ? -cmp : cmp;
  });
  // Pinned-first sorting in the flat list — matches the Briefs list's
  // pinned-first comparator behaviour. When Group By = None, the list
  // renders as a single flat table with pinned items at the top of
  // each sort field's order. The legacy "Pinned section header at
  // top" treatment now lives behind Group By = Pinned only; selecting
  // it explicitly is the user's opt-in to that view.
  const sortedWithPinnedFirst = (() => {
    if (sortColumn || groupBy !== 'none') return sortedFiltered;
    return [...sortedFiltered].sort((a, b) => {
      const ap = userPinnedSupplierIds.has(a.id) ? 0 : 1;
      const bp = userPinnedSupplierIds.has(b.id) ? 0 : 1;
      return ap - bp;
    });
  })();
  const showSections = false; // legacy auto-PINNED section retired — Group By Pinned is the explicit way in now.
  const pinnedRows = [];
  const otherRows  = sortColumn || groupBy !== 'none' ? sortedFiltered : sortedWithPinnedFirst;
  const totalPages = Math.max(1, Math.ceil(otherRows.length / PAGE_SIZE));
  const safePage   = Math.min(Math.max(1, currentPage), totalPages);

  // Group key for a supplier based on the active groupBy. Empty values
  // land in '__none' (sorted last). Mirrors the Briefs list's
  // groupKeyFor pattern so both pages compose from the same shape.
  const supplierGroupKeyFor = (s) => {
    if (groupBy === 'status')     return s.status || '__none';
    if (groupBy === 'risk')       return s.risk_flag || '__none';
    if (groupBy === 'compliance') return s.compliance_status || '__none';
    if (groupBy === 'region')     return (s.region || '').trim() || '__none';
    if (groupBy === 'pinned')     return userPinnedSupplierIds.has(s.id) ? 'pinned' : 'other';
    return null;
  };

  // Display label per (groupBy, key). Returns the human-readable header
  // string. '__none' = the catch-all for empty values per axis.
  const supplierGroupLabel = (key) => {
    if (key === '__none') {
      return {
        status:     'No status',
        risk:       'No risk tier',
        compliance: 'No compliance state',
        region:     'No region',
      }[groupBy] || 'Other';
    }
    if (groupBy === 'status') {
      return ({ active: 'Active', onboarding: 'Onboarding', inactive: 'Inactive' })[key] || key;
    }
    if (groupBy === 'risk') {
      return ({ high: 'High risk', medium: 'Medium risk', low: 'Low risk' })[key] || key;
    }
    if (groupBy === 'compliance') {
      return ({ green: 'Compliance: Green', amber: 'Compliance: Amber', red: 'Compliance: Red' })[key] || key;
    }
    if (groupBy === 'pinned') return key === 'pinned' ? 'Pinned' : 'All other matching';
    return key;
  };

  // Build the grouped buckets. null when groupBy === 'none' (the
  // page falls back to the existing pinned-first / all-other split
  // via showSections above). For all other groupBy values, returns
  // an ordered [key, items[]] array.
  const supplierGroups = (() => {
    if (groupBy === 'none') return null;
    const map = new Map();
    for (const s of sortedFiltered) {
      const k = supplierGroupKeyFor(s);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    if (groupBy === 'pinned') {
      if (!map.has('pinned')) map.set('pinned', []);
      if (!map.has('other')) map.set('other', []);
      return [['pinned', map.get('pinned')], ['other', map.get('other')]];
    }
    // Status / risk / compliance / region — order by priority (most
    // urgent first for risk + compliance; alphabetical otherwise);
    // __none sinks to the bottom.
    const priority = {
      status:     ['active', 'onboarding', 'inactive', '__none'],
      risk:       ['high', 'medium', 'low', '__none'],
      compliance: ['red', 'amber', 'green', '__none'],
    }[groupBy];
    return Array.from(map.entries()).sort((a, b) => {
      const aNone = a[0] === '__none' ? 1 : 0;
      const bNone = b[0] === '__none' ? 1 : 0;
      if (aNone !== bNone) return aNone - bNone;
      if (priority) {
        const ai = priority.indexOf(a[0]);
        const bi = priority.indexOf(b[0]);
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      }
      return a[0].localeCompare(b[0]);
    });
  })();
  const collapseAll = () => setCollapsedGroups(new Set((supplierGroups || []).map(([k]) => k)));
  const expandAll = () => setCollapsedGroups(new Set());
  const pagedOthers = otherRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const visibleRowCount = pinnedRows.length + pagedOthers.length;

  return (
    <div className="min-h-screen bg-canvas flex">

      {/* Sidebar — sticky so it stays visible while the page scrolls. */}
      <Sidebar currentInternalUser={currentInternalUser} />

      {/* Main content */}
      <div className="flex-1 flex flex-col">

        {/* Page header — inline H1 with tagline and CTAs, divider beneath.
            Matches the Dashboard pattern: the visual split is sidebar vs
            main, not header vs body. The divider is inset (mx-8) so it
            doesn't run edge-to-edge across the page. */}
        <div className="pt-8">
          <div className="px-8 flex items-start justify-between gap-6 mb-6">
            <div className="min-w-0">
              {/* FIRST PAGE ON THE BRAND TYPE SCALE (2026-08-19).
                  "Suppliers" is a NAME, not a phrase, so it takes H1 —
                  uppercase, SemiBold 600, 12% tracking, per the brand sheets.
                  A page title that reads as a sentence ("Otto can't spot that
                  page") stays on Display and stays sentence case.
                  See the scale in index.css. */}
              <h1 className="fusion-h1 text-ink-primary">Suppliers</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Manage every supplier on file — onboard new ones, track compliance and review activity.</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 pt-1">
              <HowItWorksButton to="/suppliers/how-it-works" />
              {/* Shared AddButton — + icon scales 1.24 on hover,
                  button brightens, press to scale(.97). Same
                  component the Create Brief button uses. */}
              <AddButton variant="primary" label="Add Supplier" onClick={() => navigate('/suppliers/new')} />
            </div>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Compliance Overview lives on the Compliance page (/compliance).
            This page is the supplier List View only. The activeTab ternary
            that used to wrap this was removed 2026-08-14: it was always
            'list', so the compliance branch was unreachable — and an
            unreachable second mount of ComplianceOverview is how two copies
            of a thing drift apart without anyone noticing. */}
        {/* Otto lifecycle briefing bar — top of the list, above search /
            filters. Single top state via the ladder; × hides for the day.
            Suppressed while loading and on an empty roster (the empty
            state carries its own message). */}
        {!loading && suppliers.length > 0 && !ottoBarHidden && (
          <div className="mx-8 mt-6">
            <OttoPageBar {...supplierLifecycle} onDismiss={dismissOttoBar} />
          </div>
        )}
        {/* Drill banner — only mounts when a Reporting tile sent the user
            here with a contextual filter. Bespoke copy per drill; Clear
            wipes the silent filters so the rest of the page reverts to
            its normal state. */}
        {(drillBanner || fromReporting) && (() => {
          const tones = {
            amber: { bg: 'bg-[#E89A2E]/10', border: 'border-[#E89A2E]/30', text: 'text-[#7A4E10]' },
            green: { bg: 'bg-[#92D400]/10', border: 'border-[#92D400]/30', text: 'text-fusion-forest' },
            grey:  { bg: 'bg-[#51534A]/15', border: 'border-[#DADCCD]/40', text: 'text-fusion-mid-grey' },
            teal:  { bg: 'bg-[#2E8FA1]/10', border: 'border-[#2E8FA1]/30', text: 'text-[#2E8FA1]' },
          };
          const t = tones[drillBanner?.tone] || tones.teal;
          return (
            <div className={`mx-8 mt-6 ${t.bg} border ${t.border} rounded-xl px-4 py-3 flex items-center justify-between gap-4`}>
              <p className={`font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase ${t.text}`}>
                {drillBanner
                  ? <>Showing {filtered.length} supplier{filtered.length === 1 ? '' : 's'} {drillBanner.descriptor}</>
                  : <>Arrived from Reporting</>}
              </p>
              <div className="flex items-center gap-4 flex-shrink-0">
                {fromReporting && (
                  <button
                    type="button"
                    onClick={handleBackToReporting}
                    className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-forest hover:text-fusion-forest transition-colors">
                    ← Back to Reporting
                  </button>
                )}
                {drillBanner && (
                  <button
                    type="button"
                    onClick={clearDrillFilters}
                    className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors">
                    Clear
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Toolbar — one row: search + status segment + Clear all.
            Aligned to the Briefs list pattern (list-page consistency
            brief Part A): secondary controls live inline-right of the
            search bar on both pages so the search row carries
            everything. The status segment paradigm stays Suppliers-
            only by deliberate decision (per the A2 list-paradigm call:
            different mental models — Briefs needs Group By, Suppliers
            needs lifecycle status). The Region / Risk / Compliance
            filter chips live in their respective column headers.
            Container chrome stripped to match Briefs' borderless flow. */}
        <div className="mx-8 mt-6 border-b border-line pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search suppliers by name, category, contact or region..."
              className="flex-1 min-w-[280px]" />

            {/* Status segment — fast lifecycle FILTER (composes with
                Group By below). Wrapped in the same outer pill shell
                Briefs' Group By uses so the two pages look symmetrical
                at a glance. Per the unified grouped-list brief: this
                stays as a quick-filter; Group By organises whatever's
                left after the filter applies. */}
            <div className="inline-flex items-center gap-1 bg-white border border-line rounded-xl px-3 py-1.5 whitespace-nowrap">
              <span className="fusion-field-label pr-2">Status</span>
              {[
                { value: 'active',     label: 'Active' },
                { value: 'onboarding', label: 'Onboarding' },
                { value: 'inactive',   label: 'Inactive' },
                { value: 'all',        label: 'All' },
              ].map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setStatusPill(p.value)}
                  aria-pressed={statusPill === p.value}
                  className={`font-['Metropolis'] text-[11px] font-bold tracking-[2px] uppercase px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1.5 ${
                    statusPill === p.value
                      ? 'bg-[#92D400]/15 text-fusion-forest'
                      : 'text-[#51534A] hover:text-fusion-mid-grey'
                  }`}>
                  {p.label}
                  <span className="text-[10px] font-semibold opacity-70">{pillCounts[p.value]}</span>
                </button>
              ))}
            </div>

            {/* Group By — entity-appropriate options for suppliers,
                same shared control as Briefs. Composes with the Status
                filter above. */}
            <GroupBySelect
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { value: 'none',       label: 'None' },
                { value: 'status',     label: 'Status' },
                { value: 'risk',       label: 'Risk' },
                { value: 'compliance', label: 'Compliance' },
                { value: 'region',     label: 'Region' },
                { value: 'pinned',     label: 'Pinned' },
              ]} />

            {filtersActive && (
              <button
                type="button"
                onClick={() => { setSearch(''); setStatusPill('all'); setRegionFilter([]); setRiskFilter([]); setComplianceColumnFilter([]); setSupplierTagFilter([]); clearDrillFilters(); }}
                className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors px-3 py-2">
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* TagFilterBar lifted OUT of the scroll area to match the
            Briefs list pattern — sits anchored above the column
            headers so it doesn't disappear when the table scrolls,
            and the band height + spacing match Briefs (mx-8 mt-3).
            Previously this lived inside the p-8 scroll area which
            inflated the apparent band height. */}
        <div className="mx-8 mt-3">
          <TagFilterBar
            vocabulary="supplier"
            selectedIds={supplierTagFilter}
            onChange={setSupplierTagFilter}
            matchCount={filtered.length}
            noun="supplier"
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-8 pt-4">
          {selectedForBulk.size > 0 && (
            <div className="relative flex items-center gap-3 bg-[#92D400]/[0.06] border border-[#92D400]/30 rounded-xl px-4 py-2.5 mb-4">
              <span className="font-['Metropolis'] text-xs font-bold text-ink">{selectedForBulk.size} selected</span>
              <button
                type="button"
                onClick={() => setBulkPickerOpen(o => !o)}
                className="bg-[#92D400] text-[#131313] rounded-full px-3 py-1 text-[11px] font-extrabold tracking-[0.1em] uppercase font-['Metropolis'] hover:bg-[#a4e810] transition-colors"
              >
                Apply tag ▾
              </button>
              <button
                type="button"
                onClick={() => { setSelectedForBulk(new Set()); setBulkPickerOpen(false); }}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors"
              >
                Clear selection
              </button>
              {bulkPickerOpen && (
                <TagPicker
                  vocabulary="supplier"
                  appliedTags={[]}
                  actorId={currentInternalUser?.id}
                  onApply={handleBulkApply}
                  onClose={() => setBulkPickerOpen(false)}
                />
              )}
            </div>
          )}
          {loading ? (
            <div className="py-20"><OttoLoading size="large" message="Loading suppliers" /></div>
          ) : filtered.length === 0 ? (
            filtersActive ? (
              // Empty-filter — Confused Otto + a directive that names the
              // active filters (a genuine human moment, per build #9).
              <div className="text-center py-16">
                <div className="flex justify-center mb-5"><OttoConfused size={104} /></div>
                <p className="font-['Metropolis'] text-[15px] font-bold text-ink-primary">No suppliers match those filters</p>
                <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-1.5 max-w-[44ch] mx-auto leading-relaxed">
                  {(() => {
                    const bits = [];
                    if (riskFilter.length) bits.push(`${riskFilter.join('/')} risk`);
                    if (statusPill !== 'all') bits.push(statusPill);
                    const desc = bits.length ? ` that's ${bits.join(' and ')}` : '';
                    const q = query ? ` matching “${query}”` : '';
                    return `Nothing on file${desc}${q}. Try clearing a tag or widening the status.`;
                  })()}
                </p>
                <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                  <button
                    type="button"
                    onClick={() => { setSearch(''); setStatusPill('all'); setRegionFilter([]); setRiskFilter([]); setComplianceColumnFilter([]); setSupplierTagFilter([]); clearDrillFilters(); }}
                    className="bg-fusion-green border border-fusion-green rounded-[10px] text-[#14210A] font-['Metropolis'] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5 hover:brightness-95 transition">
                    Clear filters
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/suppliers/new')}
                    className="bg-white border border-fusion-emerald rounded-[10px] text-fusion-emerald font-['Metropolis'] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5 hover:bg-fusion-emerald/[0.06] transition">
                    Add a supplier
                  </button>
                  {fromReporting && (
                    <button
                      type="button"
                      onClick={handleBackToReporting}
                      className="bg-white border border-line rounded-[10px] text-fusion-mid-grey font-['Metropolis'] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5 hover:border-[#DADCCD] transition-colors">
                      ← Back to Reporting
                    </button>
                  )}
                </div>
              </div>
            ) : (
              // Genuinely empty roster (first-run) keeps its own onboarding line.
              <div className="text-center py-20">
                <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">No suppliers yet — add your first one!</p>
              </div>
            )
          ) : (() => {
            // Empty-dash inline marker — used wherever a cell is genuinely
            // unset. 40% opacity so populated data dominates the scan.
            const Dash = () => (
              <span className="font-['Metropolis'] text-[#51534A] opacity-40">—</span>
            );

            // Sortable column header. `filter` is an optional element
            // (typically a <FilterDropdown />) rendered as a chip beneath
            // the label so per-column filters live inside their own
            // header. Sort lives on the label button so a click on the
            // filter chip doesn't also toggle sort.
            // SortHeader aligned with the Briefs list pattern: ↕ glyph
            // for sortable-inactive (replaced the old ▾ which read as a
            // filter chevron — confusing on columns that ALSO carry a
            // filter), ↑/↓ for active sort direction, and the whole
            // label tints green when this is the active sort. Filter
            // (when present) renders INLINE on the same row as a
            // sibling button, not stacked below — matches the brief
            // requirement that both list pages use one shared header
            // pattern.
            const SortHeader = ({ id, label, className, filter }) => {
              const isActive = sortColumn === id;
              const sortGlyph = isActive ? (sortDirection === 'desc' ? '↓' : '↑') : '↕';
              const handleSort = () => {
                if (sortColumn === id) {
                  // Toggle asc/desc on the same column. Third click
                  // clears the sort and returns to the default
                  // (PINNED + alphabetical) view.
                  if (sortDirection === 'asc') setSortDirection('desc');
                  else { setSortColumn(null); setSortDirection('asc'); }
                } else {
                  setSortColumn(id);
                  setSortDirection('asc');
                }
              };
              return (
                <th
                  scope="col"
                  // bg-white makes the cell opaque so rows scrolling
                  // beneath the sticky header don't show through; border-b
                  // gives the sticky header a visible separator line
                  // (border-separate means the table-level border-b on
                  // <thead> wouldn't render).
                  className={`text-left pt-5 pb-3 align-top bg-table-header border-b border-hair-admin ${className || ''}`}>
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={handleSort}
                      className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase cursor-pointer transition-colors select-none bg-transparent border-0 p-0 inline-flex items-center gap-1 ${
                        isActive ? 'text-fusion-forest' : 'text-fusion-mid-grey hover:text-fusion-mid-grey'
                      }`}>
                      {label}
                      <span className={`text-[10px] leading-none ${isActive ? 'text-fusion-forest' : 'text-fusion-mid-grey'}`} aria-hidden="true">{sortGlyph}</span>
                    </button>
                    {filter && (
                      <span className="normal-case tracking-normal">{filter}</span>
                    )}
                  </div>
                </th>
              );
            };

            // Plain (non-sortable) header used for the Pin / Avatar /
            // Contact / Actions columns where sort isn't meaningful.
            // Muted #7d7f76 matches the inactive sortable header tint so
            // every non-icon-only column reads in the same register.
            const PlainHeader = ({ label, className, ariaLabel }) => (
              <th
                scope="col"
                aria-label={ariaLabel || label}
                // Opaque bg + border-b same as SortHeader so the sticky
                // thead reads uniformly across sortable / non-sortable
                // columns.
                className={`text-left font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey pt-5 pb-3 bg-table-header border-b border-hair-admin ${className || ''}`}>
                {label}
              </th>
            );

            // Legacy SectionRow (auto-PINNED + All-other-matching
            // headers) removed in 2026-06: Group By = None is now a
            // genuine flat list with pinned-first sort, matching
            // Briefs. The headered Pinned/Others split lives behind
            // Group By = Pinned now (rendered by the unified
            // grouped-list block below).

            // Single supplier row. Used in both pinned and others
            // sections so the markup stays in one place.
            const renderSupplierRow = (supplier, idx) => {
              const isPinned = userPinnedSupplierIds.has(supplier.id);
              const bucket   = statusPillFor(supplier);
              const palette  = supplierAvatarPalette(supplier.id);
              const initials = supplierInitials(supplier.supplier_name);
              // Risk carries the semantic load (build #9): Low = neutral
              // grey, Medium = teal (was off-palette amber), High = red.
              const riskCfg = supplier.risk_flag === 'high'
                ? { label: 'High',   bg: 'bg-[#E0290A]/[0.08]', text: 'text-[#E0290A]', border: 'border-[#E0290A]/30' }
                : supplier.risk_flag === 'medium'
                ? { label: 'Medium', bg: 'bg-[#2E8FA1]/[0.08]', text: 'text-[#2E8FA1]', border: 'border-[#2E8FA1]/30' }
                : supplier.risk_flag === 'low'
                ? { label: 'Low',    bg: 'bg-[#51534A]/[0.10]', text: 'text-[#51534A]', border: 'border-[#51534A]/30' }
                : null;
              // Active = green dot; Onboarding = teal (was amber); Inactive = grey.
              const statusCfg = bucket === 'active'
                ? { label: 'Active',     dot: '#92D400', text: 'text-fusion-forest', bg: 'bg-[#92D400]/[0.08]', border: 'border-[#92D400]/30' }
                : bucket === 'onboarding'
                ? { label: 'Onboarding', dot: '#2E8FA1', text: 'text-[#2E8FA1]', bg: 'bg-[#2E8FA1]/[0.08]', border: 'border-[#2E8FA1]/30' }
                : { label: 'Inactive',   dot: '#51534A', text: 'text-[#51534A]', bg: 'bg-[#51534A]/[0.15]', border: 'border-[#DADCCD]/50' };
              const activity = formatLastActivity(supplier.last_activity_at);
              return (
                <tr
                  key={supplier.id}
                  onClick={(e) => {
                    // Clicks on the pin button, the actions menu, or
                    // anything inside the menu portal should NOT
                    // navigate. The pin/menu handlers stop their own
                    // bubbling - this catch-all guards against any
                    // descendant the pattern misses.
                    if (e.target.closest('[data-row-control]')) return;
                    navigate(`/suppliers/${supplier.id}`);
                  }}
                  className={`${idx % 2 === 1 ? 'bg-table-zebra' : 'bg-white'} border-t border-table-divider hover:bg-table-hover cursor-pointer transition-colors`}>
                  {/* Pin + bulk-select. Pin comes FIRST so the leftmost
                      visual element on the row matches the Briefs list
                      (which has the same PinButton in column 1). The
                      checkbox follows — its presence is a deliberate
                      Suppliers-only divergence powering the bulk Apply
                      tag toolbar, see the state comment at top. */}
                  <td className="py-3 pl-3 pr-1 align-middle" data-row-control>
                    <div className="flex items-center gap-2">
                      <PinButton
                        isPinned={isPinned}
                        onToggle={() => toggleSupplierPin(supplier.id)}
                        label={supplier.supplier_name || 'supplier'} />
                      <input
                        type="checkbox"
                        checked={selectedForBulk.has(supplier.id)}
                        onChange={() => toggleBulk(supplier.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${supplier.supplier_name || 'supplier'} for bulk tagging`}
                        className="w-4 h-4 accent-active-green flex-shrink-0 cursor-pointer"
                      />
                    </div>
                  </td>
                  {/* Avatar */}
                  <td className="py-3 pr-2 align-middle">
                    <div
                      aria-hidden="true"
                      style={{ background: palette.bg, color: palette.text }}
                      className="w-8 h-8 rounded-full flex items-center justify-center font-['Metropolis'] text-[11px] font-extrabold flex-shrink-0">
                      {initials}
                    </div>
                  </td>
                  {/* Supplier name + sub */}
                  <td className="py-3 pr-4 align-middle">
                    <p className="font-['Metropolis'] text-sm font-bold text-ink leading-tight">{supplier.supplier_name}</p>
                    {(supplier.category || supplier.sub_category) && (
                      <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey/70 mt-0.5">
                        {[supplier.category, supplier.sub_category].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {(() => {
                      const tlist = tagsBySupplier.get(supplier.id) || [];
                      if (tlist.length === 0) return null;
                      const shown = tlist.slice(0, 4);
                      const extra = tlist.length - shown.length;
                      return (
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          {shown.map(t => <TagChip key={t.id} tag={t} />)}
                          {extra > 0 && <span className="font-['Metropolis'] text-[10px] font-bold text-[#51534A]">+{extra} more</span>}
                        </div>
                      );
                    })()}
                  </td>
                  {/* Contact */}
                  <td className="py-3 pr-4 align-middle">
                    <p className="font-['Metropolis'] text-[13px] font-semibold text-ink leading-tight">
                      {supplier.main_contact_name || <Dash />}
                    </p>
                    <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey/75 mt-0.5 truncate max-w-[220px]">
                      {supplier.main_contact_email || <Dash />}
                    </p>
                  </td>
                  {/* Region */}
                  <td className="py-3 pr-4 align-middle">
                    {supplier.region ? (
                      <span className="font-['Metropolis'] text-[13px] text-ink">{supplier.region}</span>
                    ) : <Dash />}
                  </td>
                  {/* Status */}
                  <td className="py-3 pr-4 align-middle">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}>
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: statusCfg.dot }} />
                        {statusCfg.label}
                      </span>
                      {supplier.on_hold && (
                        <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#2E8FA1]/[0.08] border border-[#2E8FA1]/30 text-[#2E8FA1]">On hold</span>
                      )}
                      {supplier.do_not_use_status === 'Do Not Use' && (
                        <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#E0290A]/[0.08] border border-[#E0290A]/30 text-[#E0290A]">Do not use</span>
                      )}
                      {supplier.do_not_use_status === 'Use With Caution' && (
                        <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#2E8FA1]/[0.08] border border-[#2E8FA1]/30 text-[#2E8FA1]">Caution</span>
                      )}
                      {supplier.is_record_only && (
                        <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#2E8FA1]/[0.08] border border-[#2E8FA1]/30 text-[#2E8FA1]">Record only</span>
                      )}
                      {supplier.preferred_supplier === 'Preferred Supplier' && (
                        <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#92D400]/[0.08] border border-[#92D400]/30 text-fusion-forest">Preferred</span>
                      )}
                      {(badgesBySupplier.get(supplier.id) || []).map(b => {
                        const cat = badgeCategoryMap.get(b.category_id);
                        if (!cat) return null;
                        return (
                          <BadgeChip key={`${b.category_id}-${b.tier}`} tier={b.tier} category={cat.name} />
                        );
                      })}
                    </div>
                  </td>
                  {/* Risk */}
                  <td className="py-3 pr-4 align-middle">
                    {riskCfg ? (
                      <span className={`inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full border ${riskCfg.bg} ${riskCfg.text} ${riskCfg.border}`}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                          <line x1="4" y1="22" x2="4" y2="15" />
                        </svg>
                        {riskCfg.label}
                      </span>
                    ) : <Dash />}
                  </td>
                  {/* Compliance — hexagonal badge replaces the previous
                      coloured pill; reads at a glance across long lists.
                      Null compliance_status (never assessed — typically
                      still in onboarding) renders the badge's not_set
                      state so the column always shows a hexagon and
                      stays one consistent visual signal. */}
                  <td className="py-3 pr-4 align-middle">
                    <ComplianceBadge state={supplier.compliance_status || 'not_set'} size="sm" />
                  </td>
                  {/* Last activity */}
                  <td className="py-3 pr-4 align-middle">
                    {activity ? (
                      <span className={`font-['Metropolis'] text-[11px] ${activity.tone}`}>{activity.text}</span>
                    ) : <Dash />}
                  </td>
                  {/* Actions (three-dot menu) */}
                  <td className="py-3 pr-3 align-middle" data-row-control>
                    <div className="relative inline-block" ref={rowActionsFor === supplier.id ? rowActionsRef : null}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setRowActionsFor(rowActionsFor === supplier.id ? null : supplier.id); }}
                        aria-haspopup="true"
                        aria-expanded={rowActionsFor === supplier.id}
                        title="Row actions"
                        className="p-1.5 rounded text-[#51534A] hover:text-ink hover:bg-white/[0.05] transition-colors">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <circle cx="12" cy="5"  r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>
                      {rowActionsFor === supplier.id && (
                        <div
                          role="menu"
                          className="absolute right-0 top-full mt-1 z-20 w-44 bg-white border border-line rounded-xl shadow-lg overflow-hidden">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => { e.stopPropagation(); setRowActionsFor(null); navigate(`/suppliers/${supplier.id}`); }}
                            className="w-full text-left px-4 py-2.5 font-['Metropolis'] text-xs text-ink hover:bg-active-green/10 hover:text-active-green transition-colors">
                            View supplier
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => { toggleSupplierPin(supplier.id, e); setRowActionsFor(null); }}
                            className="w-full text-left px-4 py-2.5 font-['Metropolis'] text-xs text-ink hover:bg-active-green/10 hover:text-active-green transition-colors border-t border-line">
                            {isPinned ? 'Unpin' : 'Pin'}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => { e.stopPropagation(); setRowActionsFor(null); navigate('/briefs/new', { state: { preselectSupplierId: supplier.id } }); }}
                            className="w-full text-left px-4 py-2.5 font-['Metropolis'] text-xs text-ink hover:bg-active-green/10 hover:text-active-green transition-colors border-t border-line">
                            Send brief…
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            };

            return (
              // Flush/borderless table per B1 of the list-page
              // consistency brief. The bordered card chrome that
              // previously wrapped the table (rounded-xl + dark fill +
              // border) was visually heavier than the Briefs list and
              // didn't earn its space on a dense data table. Dropped
              // here; both pages now share one frame treatment.
              <div className="overflow-hidden">
                {/* Outer chrome (summary row, sticky thead, group
                    headers with accent + caret + count + meta, row
                    dividers) now driven by the shared GroupedListTable
                    component so Briefs and Suppliers can't drift on
                    chrome again. Suppliers passes its entity-specific
                    column header config (thead) + renderSupplierRow +
                    grouping config; the component does everything else. */}
                <GroupedListTable
                  thead={<>
                    <PlainHeader label="" ariaLabel="Pin" className="pl-3 pr-1 w-8" />
                    <PlainHeader label="" ariaLabel="Avatar" className="pr-2 w-11" />
                    <SortHeader id="name"       label="Supplier"     className="pr-4 min-w-[220px]" />
                    <PlainHeader label="Contact" className="pr-4 min-w-[180px]" />
                    <SortHeader id="region"     label="Region"       className="pr-4 w-[140px]"
                      filter={
                        <FilterDropdown label="Region" hideLabel options={regionOptions} selected={regionFilter} onChange={setRegionFilter} />
                      } />
                    <SortHeader id="status"     label="Status"       className="pr-4 w-[180px]" />
                    <SortHeader id="risk"       label="Risk"         className="pr-4 w-[120px]"
                      filter={
                        <FilterDropdown
                          label="Risk"
                          hideLabel
                          options={[
                            { value: 'high',   label: 'High' },
                            { value: 'medium', label: 'Medium' },
                            { value: 'low',    label: 'Low' },
                          ]}
                          selected={riskFilter}
                          onChange={setRiskFilter} />
                      } />
                    <SortHeader id="compliance" label="Compliance"   className="pr-4 w-[160px]"
                      filter={
                        <FilterDropdown
                          label="Compliance"
                          hideLabel
                          options={[
                            { value: 'green', label: 'Green' },
                            { value: 'amber', label: 'Amber' },
                            { value: 'red',   label: 'Red' },
                          ]}
                          selected={complianceColumnFilter}
                          onChange={setComplianceColumnFilter} />
                      } />
                    <SortHeader id="activity"   label="Last activity" className="pr-4 w-[140px]" />
                    <PlainHeader label="" ariaLabel="Actions" className="pr-3 w-[40px]" />
                  </>}
                  colSpan={10}
                  groups={supplierGroups}
                  rows={pagedOthers}
                  renderRow={renderSupplierRow}
                  getRowKey={(s) => s.id}
                  getGroupLabel={(key) => supplierGroupLabel(key)}
                  getGroupMeta={(key) => (groupBy === 'pinned' && key === 'pinned') ? null : 'Sorted A–Z'}
                  noun="supplier"
                  collapsedGroups={collapsedGroups}
                  onToggleGroup={toggleGroupCollapsed}
                  onCollapseAll={collapseAll}
                  onExpandAll={expandAll}
                  filteredCount={filtered.length}
                  isPinnedGroup={(key) => groupBy === 'pinned' && key === 'pinned'}
                />

                {/* Footer: row count + pagination. Pinned rows are
                    always fully visible so the count reflects the
                    paginated others slice plus all pinned. */}
                <div className="flex flex-wrap items-center justify-between gap-3 bg-white/[0.02] border-t border-line px-4 py-3">
                  <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">
                    Showing <strong className="text-ink font-bold">{visibleRowCount}</strong> of <strong className="text-ink font-bold">{filtered.length}</strong> supplier{filtered.length === 1 ? '' : 's'}
                    {showSections && pinnedRows.length > 0 && (
                      <> · <strong className="text-ink font-bold">{pinnedRows.length}</strong> pinned</>
                    )}
                  </p>
                  {totalPages > 1 && (
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={safePage === 1}
                        className="w-7 h-7 rounded border border-line text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold flex items-center justify-center hover:border-[#DADCCD] hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        ‹
                      </button>
                      {(() => {
                        // Build a compact page number list with ellipses
                        // — first, last, current, current±1.
                        const pages = new Set([1, totalPages, safePage, safePage - 1, safePage + 1]);
                        const list = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
                        const out = [];
                        for (let i = 0; i < list.length; i++) {
                          if (i > 0 && list[i] - list[i - 1] > 1) out.push('…');
                          out.push(list[i]);
                        }
                        return out.map((p, idx) => p === '…' ? (
                          <span key={`gap-${idx}`} className="font-['Metropolis'] text-[11px] text-[#51534A] px-1">…</span>
                        ) : (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setCurrentPage(p)}
                            className={`w-7 h-7 rounded border font-['Metropolis'] text-xs font-semibold flex items-center justify-center transition-colors ${
                              p === safePage
                                ? 'bg-[#92D400] border-[#92D400] text-[#131313]'
                                : 'border-line text-fusion-mid-grey hover:border-[#DADCCD] hover:text-ink'
                            }`}>
                            {p}
                          </button>
                        ));
                      })()}
                      <button
                        type="button"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages}
                        className="w-7 h-7 rounded border border-line text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold flex items-center justify-center hover:border-[#DADCCD] hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        ›
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
